import ArticleLayout from '../../components/ArticleLayout';

export default function RadiatorUdskiftningPris() {
  return (
    <ArticleLayout
      title="Hvad koster udskiftning af radiator eller ventil? Prisspænd og faktorer"
      metaTitle="Radiator udskiftning pris: Hvad koster det hos VVS?"
      metaDescription="Hvad koster det at få skiftet en radiator eller en termostatventil? Se realistiske prisspænd for materialer og VVS-arbejdsløn samt hvad der driver prisen."
      canonicalPath="/radiator-udskiftning-pris"
      introLead="Prisen for at udskifte en radiator afhænger grundlæggende af tre poster: radiatorens dimension og ydelse, tilpasning af rørføringen og arbejdslønnen til VVS-montøren."
      introAnswer="En komplet udskiftning af en almindelig radiator i et enfamiliehus koster typisk mellem 2.500 kr. og 5.500 kr. inkl. moms og montering. Skal du blot have skiftet en slidt termostatventil eller en fastgroet pakdåse, ligger prisen normalt i spændet 1.200 kr. til 2.200 kr. for materialer og montørtid."
      tableOfContents={[
        { id: 'tjekliste', title: '1. Tjekliste: Før du bestiller VVS-tilbud' },
        { id: 'prisspaend-oversigt', title: '2. Vejledende prisspænd i tabel' },
        { id: 'hvad-driver-prisen', title: '3. Hvilke faktorer driver prisen op?' },
        { id: 'goer-det-selv-regler', title: '4. Hvad må du lave selv?' },
        { id: 'faq', title: '5. Ofte stillede spørgsmål' },
      ]}
      checklistTitle="Tjekliste: Det bør du afklare inden udskiftning"
      checklist={[
        {
          title: 'Mål radiatorens højde, længde og dybde i millimeter',
          description: 'Standardhøjder er typisk 300, 400, 550, 600 eller 900 mm; målene afgør, om de eksisterende rør kan genbruges uden forlængelse.',
        },
        {
          title: 'Undersøg radiatorens type (type 11, 21, 22 eller 33)',
          description: 'Type 11 har ét panel, type 22 har to paneler med to konvektionsriste, og type 33 har tre paneler med højeste varmeeffekt.',
        },
        {
          title: 'Notér placeringen af fremløbs- og returrør',
          description: 'Er tilslutningen i samme side, i hver sin ende eller fra gulvet i bunden (integreret ventilradiator)?',
        },
        {
          title: 'Undersøg anlæggets type og mulighed for afspærring',
          description: 'Find ud af om huset har egne stophaner, eller om anlægget i en hel etageejendom skal tømmes for vand.',
        },
        {
          title: 'Indhent faste priser med specificeret materiale- og timeløn',
          description: 'Bed om skriftlig specifikation af radiator, Danfoss ventil, montering, aftapning og bortskaffelse af den gamle radiator.',
        },
      ]}
      sections={[
        {
          id: 'prisspaend-oversigt',
          title: 'Vejledende prisspænd for materialer og VVS-arbejde',
          content: (
            <>
              <p>
                Herunder er opstillet et typisk estimat baseret på gængse markedspriser i Danmark (inkl. moms):
              </p>
              <div className="overflow-x-auto my-4">
                <table className="w-full text-left text-sm border border-[#E6DED5]">
                  <thead className="bg-[#F3E6DC] text-[#221E1B]">
                    <tr>
                      <th className="p-3 font-semibold border-b border-[#E6DED5]">Opgave</th>
                      <th className="p-3 font-semibold border-b border-[#E6DED5]">Materialepris</th>
                      <th className="p-3 font-semibold border-b border-[#E6DED5]">Arbejdsløn (VVS)</th>
                      <th className="p-3 font-semibold border-b border-[#E6DED5]">Samlet prisspænd</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E6DED5]">
                    <tr>
                      <td className="p-3 font-medium">Ny Danfoss termostatføler (gør-det-selv)</td>
                      <td className="p-3 font-mono">150 – 350 kr.</td>
                      <td className="p-3 font-mono">0 kr.</td>
                      <td className="p-3 font-mono font-bold">150 – 350 kr.</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-medium">Udskiftning af termostatventil inkl. føler</td>
                      <td className="p-3 font-mono">350 – 700 kr.</td>
                      <td className="p-3 font-mono">800 – 1.500 kr.</td>
                      <td className="p-3 font-mono font-bold">1.200 – 2.200 kr.</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-medium">Ny standard panelradiator (udskiftning 1:1)</td>
                      <td className="p-3 font-mono">800 – 2.200 kr.</td>
                      <td className="p-3 font-mono">1.500 – 3.000 kr.</td>
                      <td className="p-3 font-mono font-bold text-[#B5531E]">2.500 – 5.500 kr.</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-medium">Ny radiator med omlægning af rørføring</td>
                      <td className="p-3 font-mono">1.200 – 3.000 kr.</td>
                      <td className="p-3 font-mono">3.000 – 5.500 kr.</td>
                      <td className="p-3 font-mono font-bold">4.500 – 8.500 kr.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          ),
        },
        {
          id: 'hvad-driver-prisen',
          title: 'Hvad driver prisen op på opgaven?',
          content: (
            <>
              <p>
                Der er flere praktiske forhold, der kan forskyde prisen op i den øvre ende af skalaen:
              </p>
              <p>
                <strong>1. Tømning og genopfyldning af anlægget:</strong> Kan radiatoren isoleres på lokale returventiler, tager udskiftningen ofte under 1,5 time. Skal hele boligens eller opgangens centralvarmesystem aftappes, afluftes og genopfyldes, tillægges der nemt 1–2 ekstra arbejdstimer.
              </p>
              <p>
                <strong>2. Indfrysning af rør:</strong> I etageejendomme vælger installatøren ofte at fastfryse vandet i rørene med en professionel frysemaskine, så resten af ejendommen ikke behøver lukkes ned. Dette sparer tid, men udløser et mindre maskintillæg.
              </p>
              <p>
                <strong>3. Tilpasning af rør:</strong> Hvis den nye radiator ikke har nøjagtig samme centerafstand mellem tilslutningerne som den gamle, skal stålrørene skæres til, forlænges eller have nye bøjninger.
              </p>
            </>
          ),
        },
        {
          id: 'goer-det-selv-regler',
          title: 'Hvad må du selv udføre ifølge reglerne?',
          content: (
            <>
              <p>
                Du må gerne selv klikke et nyt termostathoved på en eksisterende ventil, eller skifte en pakdåse på en Danfoss-ventil med indbygget bagtætning.
              </p>
              <p>
                Montering af selve ventilhuset eller nye rørforskruninger på varmeanlægget bør overlades til en autoriseret VVS-installatør, da en lækage kan forårsage massive vandskader, som forsikringen kan afvise at dække, hvis arbejdet er udført fagligt ukorrekt.
              </p>
            </>
          ),
        },
      ]}
      proCallout={{
        title: 'Husk bortskaffelse og gulvbeskyttelse',
        content: (
          <p>
            En udtjent støbejernsradiator vejer typisk 40–80 kg og indeholder stærkt misfarvet vand med rust og magnetit. Sørg for at aftale på forhånd med installatøren, om bortbæring, gulvafdækning og miljøgodkendt bortskaffelse af den gamle radiator er inkluderet i tilbuddet.
          </p>
        ),
      }}
      faq={[
        {
          q: 'Hvor lang tid tager det at skifte en radiator for en VVS’er?',
          a: 'En standard 1:1 udskiftning, hvor rørdimensioner passer og anlægget let kan afspærres, tager typisk mellem 1 og 2 timer.',
        },
        {
          q: 'Kan man få håndværkerfradrag for udskiftning af radiatorer?',
          a: 'Reglerne for service- og håndværkerfradrag ændres jævnligt. Energiforbedrende tiltag som installation af termostater og optimering af varmeanlæg har historisk været omfattet i visse ordninger, men tjek altid de aktuelle regler på Skat.dk.',
        },
        {
          q: 'Hvornår kan det betale sig at skifte en gammel radiator?',
          a: 'Hvis radiatoren er gennemtæret, drypper fra samlinger, eller hvis du skifter til varmepumpe og har brug for en større radiatorflade med højere konvektionseffekt ved lave fremløbstemperaturer.',
        },
      ]}
      internalLinks={[
        { to: '/radiator-varmer-ikke', text: 'Radiatoren varmer ikke', desc: 'tjek fejlkilder inden udskiftning' },
        { to: '/gammel-radiator-varmer-ikke', text: 'Gammel radiator varmer ikke', desc: 'tilstoppet, rusten eller forkert dimensioneret' },
        { to: '/radiator-laekker', text: 'Radiatoren lækker vand', desc: 'akut afspærring ved utætheder' },
        { to: '/radiator-termostat', text: 'Radiatortermostater forklaret', desc: 'vælg den rigtige model' },
      ]}
      sources={[
        { url: 'https://www.bolius.dk/valg-af-radiator-19095', label: 'Valg af radiator - sådan sikrer du, at du vælger rigtigt' },
        { url: 'https://sparenergi.dk/privat/spar-energi-i-hverdagen/spar-paa-varme-og-vand-i-hjemmet', label: 'Normalt varmeforbrug i kWh (+ fjernvarme i MWh) | SparEnergi' },
      ]}
    />
  );
}
